import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import { CreateAccidentIssueInput, CreateAccidentIssueResponse, MutationCreateAccidentIssueBulkArgs } from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';

import { CreateAccidentIssue } from '@/features/AccidentHistory/api/queries/CreateAccidentIssue';
import { TypedDocumentNode } from '@apollo/client';
import { getProperty } from '@/utils';
import graphApi from '@/api';

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof CreateAccidentIssueInput]?: any;
}[];

interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<CreateAccidentIssueResponse>;
}

// SearchAccidentIssue Service
export const { createAccidentIssue, useCreateAccidentIssue } = ((): {
  createAccidentIssue: ServiceMethod;
  useCreateAccidentIssue: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch] = graphApi(CreateAccidentIssue).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (
        payload?: Record<keyof CreateAccidentIssueInput, any>[],
        options?: ConnectionOptions
      ): Promise<CreateAccidentIssueResponse> => {
        const values: CreateAccidentIssueInput[] = payload.map((item) => ({
          accidentDatetime: item?.accidentDatetime || null,
          anyoneInjured: item?.anyoneInjured || null,
          damageToOtherVehicles: item?.damageToOtherVehicles || null,
          canVehicleBeDriven: item?.canVehicleBeDriven || null,
          description: item?.description || null,
          didDriverCallHelp: item?.didDriverCallHelp || null,
          medicalNeeded: item?.medicalNeeded || null,
          safetyNotified: item?.safetyNotified || null,
          samsaraCameraWorking: item?.samsaraCameraWorking || null,
          specificLocation: item?.specificLocation || null,
          stationText: item?.stationText || null,
          wasCeoCalled: item?.wasCeoCalled || null,
          wasCrewInvolved: item?.wasCrewInvolved || null,
          wasDriverAtFault: item?.wasDriverAtFault || null,
          wasThirdPartyInvolved: item?.wasThirdPartyInvolved || null,
          additionalDriverScheduled: item?.additionalDriverScheduled ?? null,
          dispatcherContactedManager: item?.dispatcherContactedManager ?? null,
          driverContactedManager: item?.driverContactedManager ?? null,
          infoOfCrewMembers: item?.infoOfCrewMembers || null,
          infoOfInjuredParties: item?.infoOfInjuredParties || null,
          thirdPartyInformation: item?.thirdPartyInformation || null,
          typeOfMedicalAttention: item?.typeOfMedicalAttention || null,
          vehicleId: item?.vehicleId || null,
          dispatcherId: item?.dispatcherId || null,
          driverId: item?.driverId || null,
          tripId: item?.tripId || null,
          safetyId: item?.safetyId || null,
          outboundFlightNumber: item?.outboundFlightNumber || null,
          outboundDatetime: item?.outboundDatetime || null,
          outboundFlightName: item?.outboundFlightName || null,
          documents: item?.documents || [],
        }));
        const args: MutationCreateAccidentIssueBulkArgs = {
          input: {
            values,
          },
        };
        const opts = { merge: options?.merge ? 'createAccidentIssueBulk' : undefined };
        const res = await fn(args, opts);
        return getProperty('createAccidentIssueBulk.output', res);
      }
  );
  const useService = createGraphApiHook(fetch);
  return {
    createAccidentIssue: fetch,
    useCreateAccidentIssue: useService,
  };
})();
